import { useEffect, useMemo, useState } from 'react';
import { useStripeFinancials } from '../../context/StripeFinancialsContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../ui';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const shortMoney = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1000) {
    return (n < 0 ? '-$' : '$') + (abs / 1000).toFixed(abs >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'k';
  }
  return (n < 0 ? '-$' : '$') + Math.round(abs);
};

interface BarsProps {
  labels: string[];
  a: number[];
  b: number[];
  height?: number;
  aLabel?: string;
  bLabel?: string;
}

/** Dual-series bar chart — rust leads, green supports; hover dims the rest. */
function DualBars({ labels, a, b, height = 180, aLabel = 'Revenue', bLabel = 'Expenses' }: BarsProps) {
  const max = Math.max(...a, ...b, 1) * 1.1;
  const n = labels.length;
  const [hover, setHover] = useState<number | null>(null);

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-muted)',
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'var(--chart-1)' }} />
          {aLabel}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-muted)',
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'var(--chart-3)' }} />
          {bLabel}
        </span>
        <span className="t-num" style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {hover !== null ? `${labels[hover]} · ${shortMoney(a[hover])} / ${shortMoney(b[hover])}` : ' '}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${n}, 1fr)`,
          gap: '6px',
          alignItems: 'end',
          height: `${height}px`,
        }}
      >
        {labels.map((lab, i) => (
          <div
            key={lab + i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            style={{
              display: 'flex',
              gap: '2px',
              alignItems: 'flex-end',
              height: '100%',
              cursor: 'default',
              opacity: hover === null || hover === i ? 1 : 0.45,
              transition: 'opacity var(--dur-base) var(--ease-out)',
            }}
          >
            <div
              style={{
                flex: 1,
                height: `${Math.max(2, (a[i] / max) * 100)}%`,
                background: 'var(--chart-1)',
                borderRadius: '3px 3px 0 0',
                transition: 'height var(--dur-slow) var(--ease-out)',
              }}
            />
            <div
              style={{
                flex: 1,
                height: `${Math.max(2, (b[i] / max) * 100)}%`,
                background: 'var(--chart-3)',
                borderRadius: '3px 3px 0 0',
                opacity: 0.75,
                transition: 'height var(--dur-slow) var(--ease-out)',
              }}
            />
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${n}, 1fr)`, gap: '6px', marginTop: '7px' }}>
        {labels.map((lab, i) => (
          <span
            key={lab + i}
            className="t-num"
            style={{ fontSize: '10px', color: 'var(--text-faint)', textAlign: 'center' }}
          >
            {lab}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RevenueChart() {
  const { transactions } = useStripeFinancials();
  const [expensesByMonth, setExpensesByMonth] = useState<Map<string, number>>(new Map());

  // Monthly expenses come from financial_expenses (same source as the P&L)
  useEffect(() => {
    let cancelled = false;
    const fetchExpenses = async () => {
      try {
        const { data, error } = await supabase.from('financial_expenses').select('month, year, amount');
        if (error) throw error;
        if (cancelled) return;
        const map = new Map<string, number>();
        data?.forEach((exp) => {
          map.set(`${exp.year}-${exp.month}`, Number(exp.amount));
        });
        setExpensesByMonth(map);
      } catch (err) {
        console.error('Error fetching expenses for chart:', err);
      }
    };
    fetchExpenses();
    return () => {
      cancelled = true;
    };
  }, []);

  // Trailing 12 months from real transactions + expense records
  const { labels, revenue, expenses } = useMemo(() => {
    const now = new Date();
    const months: Array<{ year: number; month: number }> = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }

    const revenueByMonth = new Map<string, number>();
    transactions.forEach((txn) => {
      if (txn.amount <= 0) return;
      const date = new Date(txn.created_at);
      const key = `${date.getFullYear()}-${date.getMonth() + 1}`;
      revenueByMonth.set(key, (revenueByMonth.get(key) || 0) + txn.amount / 100);
    });

    return {
      labels: months.map((m) => MONTH_NAMES[m.month - 1]),
      revenue: months.map((m) => revenueByMonth.get(`${m.year}-${m.month}`) || 0),
      expenses: months.map((m) => expensesByMonth.get(`${m.year}-${m.month}`) || 0),
    };
  }, [transactions, expensesByMonth]);

  return (
    <Card eyebrow="Revenue vs Expenses" title="Trailing 12 months">
      <DualBars labels={labels} a={revenue} b={expenses} />
    </Card>
  );
}
